/**
 * MotionDetailPage — Motion 一等实体详情页（V14 motion-centric）。
 *
 * Route: `/ivt/motions/:motionId`
 *
 * 单次 GetMotionOverview(motionId) 调用拉回全部数据。渲染：
 *   1. Breadcrumb（Motions > name）
 *   2. Header + Refresh
 *   3. Information 卡片（KPI Grid + SFDC Stage Donut + Legend）
 *   4. Pipeline List 表格（accelerateOpps, multi-select, sortable）
 *
 * Figma: Rocky-Assistant PAGE "IVT" node 1920-15353.
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  BreadcrumbGroup,
  Button,
  Header,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react';
import {
  getIvtClient,
  GetAssetCommand,
  GetMotionCommand,
  GetMotionOverviewCommand,
  type GetMotionOverviewCommandOutput,
} from '../../client/IvtHubClient';
import InformationCard from './components/InformationCard';
import MotionEditModal from './components/MotionEditModal';
import PipelineTable from './components/PipelineTable';
import './MotionDetailPage.css';

export default function MotionDetailPage() {
  const { motionId } = useParams<{ motionId: string }>();
  const navigate = useNavigate();
  // Return to whichever Motions sub-tab the user came from (Motion List vs
  // Pipeline List): the link that opened this page passes { state: { fromTab } }.
  // Falls back to the Motion List tab for a fresh/deep-linked visit.
  const location = useLocation();
  const fromTab = (location.state as { fromTab?: string } | null)?.fromTab === 'pipeline' ? 'pipeline' : 'motions';
  const motionsHref = `/ivt/motions?tab=${fromTab}`;

  const [overview, setOverview] = useState<GetMotionOverviewCommandOutput | null>(null);
  const [assets, setAssets] = useState<
    Array<{
      assetId: string;
      assetName: string;
      useCase?: string;
      subTopics?: string[];
      sfdcTag?: string;
    }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!motionId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const out = await getIvtClient().send(
          new GetMotionOverviewCommand({ motionId }),
        );
        if (!cancelled) setOverview(out);
      } catch (e) {
        if (!cancelled) setError((e as Error).message ?? 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [motionId, reloadKey]);

  // Fetch asset info: GetMotion → assetIds → GetAsset per id (non-blocking)
  useEffect(() => {
    if (!motionId) return;
    let cancelled = false;
    (async () => {
      try {
        const motionOut = await getIvtClient().send(new GetMotionCommand({ motionId }));
        if (cancelled) return;
        const assetIds = motionOut.motion?.assetIds ?? [];
        if (assetIds.length === 0) return;

        const details = await Promise.all(
          assetIds.map((assetId) =>
            getIvtClient().send(new GetAssetCommand({ assetId })).catch(() => null)
          ),
        );
        if (cancelled) return;

        const rows = details
          .filter((d): d is NonNullable<typeof d> => d?.asset != null)
          .map((d) => {
            const a = d.asset!;
            const subTopics = (a.topicLinks ?? [])
              .map((t) => t.subTopicName)
              .filter((s): s is string => !!s);
            return {
              assetId: a.assetId ?? '',
              assetName: a.name ?? a.assetId ?? '',
              useCase: a.useCase ?? undefined,
              subTopics: subTopics.length > 0 ? subTopics : undefined,
              sfdcTag: a.sfdcTag ?? undefined,
            };
          });
        setAssets(rows);
      } catch {
        // non-blocking — asset info is supplementary
      }
    })();
    return () => { cancelled = true; };
  }, [motionId, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  if (!motionId) {
    return (
      <div className="ivt-motion-detail-page">
        <Alert type="error">缺少 motionId 参数</Alert>
      </div>
    );
  }

  return (
    <div className="ivt-motion-detail-page">
      <BreadcrumbGroup
        items={[
          { text: 'Motions', href: motionsHref },
          { text: overview?.name ?? motionId, href: '' },
        ]}
        onFollow={(e) => {
          e.preventDefault();
          if (e.detail.href) navigate(e.detail.href);
        }}
      />

      <div className="ivt-motion-detail-page__header">
        <Header
          variant="h2"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="normal"
                iconName="edit"
                onClick={() => setEditing(true)}
                disabled={loading || !overview}
              >
                Edit
              </Button>
              <Button variant="normal" iconName="refresh" onClick={reload} loading={loading}>
                Refresh
              </Button>
            </SpaceBetween>
          }
        >
          {overview?.name ?? motionId}
        </Header>
      </div>

      {editing && overview && (
        <MotionEditModal
          motionId={motionId}
          currentName={overview.name ?? ''}
          onClose={() => setEditing(false)}
          onSaved={(newName) => {
            setOverview((prev) => (prev ? { ...prev, name: newName } : prev));
          }}
        />
      )}

      {loading && !overview && (
        <div className="ivt-motion-detail-page__loading">
          <Spinner size="large" />
          <span>加载 Motion 详情…</span>
        </div>
      )}

      {error && (
        <Alert
          type="error"
          header="无法加载 Motion"
          action={<Button onClick={reload}>重试</Button>}
        >
          {error}
        </Alert>
      )}

      {overview && (
        <SpaceBetween size="l">
          <InformationCard
            pipelineTotal={overview.pipelineTotal ?? 0}
            totalArr={overview.totalArr ?? 0}
            taggedPipeline={overview.taggedPipeline ?? undefined}
            pendingTagPipeline={overview.pendingTagPipeline ?? undefined}
            opps={overview.accelerateOpps ?? []}
            assets={assets}
          />
          <PipelineTable
            motionId={motionId}
            opps={overview.accelerateOpps ?? []}
            onReload={reload}
          />
        </SpaceBetween>
      )}
    </div>
  );
}
